import { styled, useTheme } from "@mui/system";
import { LineData } from "lightweight-charts";
import { Link as RouterLink } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import RightArrowIcon from "src/icons/portfolio/RightArrowIcon";
import { ChartType } from "src/utils/types";
import {
  BEST_PERFORMERS_TOOLTIP,
  IBalanceData,
  ICompareOptions,
  ITimePeriodOption,
} from "../defaults";
import BalanceButton from "./BalanceButton";
import BalanceChart from "./BalanceChart";
import BalanceOverviewChange from "./BalanceOverviewChange";
import DonutChart from "./DonutChart";
import useDeviceSizes from "../hooks/useDeviceSizes";

export const BalanceWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "24px",
  alignSelf: "stretch",
  justifyContent: "space-between",

  [theme.breakpoints.down(720)]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

export const BalanceFunds = styled("div")(({ theme }) => ({
  display: "flex",
  width: "239px",
  flexDirection: "column",
  gap: "24px",
  alignItems: "flex-start",
  alignSelf: "stretch",

  [theme.breakpoints.down(720)]: {
    width: "100%",
    height: "154px",
  },
}));

export const BalanceHeading = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",

  [theme.breakpoints.down(720)]: {
    alignSelf: "stretch",
  },
}));

export const BalanceText = styled(RouterLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "14px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.7,
  },

  [theme.breakpoints.down(720)]: {
    justifyContent: "space-between",
    alignItems: "flex-start",
    alignSelf: "stretch",
  },
}));

export const BalanceTextAmount = styled("div")({
  fontWeight: 600,
  fontSize: "26px",
});

export const BalanceTextPercent = styled("div")({
  fontSize: "14px",
});

export const BalanceButtons = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
});

export const BestPerformer = styled("div")(({ theme }) => ({
  display: "flex",
  maxWidth: "800px",
  padding: "16px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  flex: "1 0 0",
  borderRadius: "10px",
  background: theme.palette.background.paper,
  boxShadow: `0px 4px 20px 0px ${theme.palette.secondary.contrastText}`,
  fontWeight: 500,

  [theme.breakpoints.between(720, 1440)]: {
    maxWidth: "800px",
  },

  [theme.breakpoints.down(720)]: {
    maxWidth: "100%",
    alignSelf: "stretch",
  },
}));

export const BestPerformerHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  gap: "8px",
  fontSize: "14px",
});

export const BalanceTableWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  gap: "12px",
  width: "100%",

  [theme.breakpoints.down(720)]: {
    gap: "40px",
  },
}));

export const BalanceTable = styled("table")({
  width: "100%",
  borderSpacing: "0px 8px",
  fontSize: "14px",
});

interface BalanceTableCellProps {
  firstCell?: boolean;
  align?: "center" | "left" | "right";
}
export const BalanceTableCell = styled("td")<BalanceTableCellProps>(
  ({ firstCell, align, theme }) => ({
    padding: firstCell ? "0px 8px 0px 0px" : "0px 12px",
    textAlign: align ?? "left",

    [theme.breakpoints.down(720)]: {
      padding: firstCell ? "0px 8px 0px 0px" : "0px 8px",
      textAlign: "left",
      width: " calc(100% / 2)",
    },
  }),
);

export const BalanceTableRowIcon = styled(RouterLink)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
});

export const BalanceChartWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  height: "130px",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "stretch",
  position: "relative",
  fill: "linear-gradient(178deg, #F8A0A0 -237.01%, #FFF 102.55%)",

  [theme.breakpoints.between(720, 1440)]: {
    paddingRight: "12px",
  },
}));

export const BalanceChartTimeFilter = styled("div")({
  display: "flex",
  width: "255.223px",
  alignItems: "flex-start",
  position: "absolute",
  bottom: -15,
  left: 0,
  zIndex: 99,
});

interface BalanceChartTimeFilterOptionProps {
  selected: boolean;
}
export const BalanceChartTimeFilterOption = styled("div")<BalanceChartTimeFilterOptionProps>(
  ({ selected }) => ({
    width: "36px",
    fontSize: "12px",
    flexShrink: 0,
    cursor: "pointer",
    userSelect: "none",

    // conditionals
    color: selected ? "#FC4C4C" : "",
    textDecorationLine: selected ? "underline" : "none",

    "&:hover": {
      color: "#FC4C4C",
      textDecorationLine: "underline",
    },
  }),
);

export const BalanceChartTimeFilterOptionDropdown = styled("div")(({ theme }) => ({
  display: "flex",
  width: "120px",
  padding: "12px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  borderRadius: "0px 0px 8px 8px",
  background: theme.palette.background.paper,
  position: "relative",
  top: 20,
  right: 25,

  /* Shadow (XL)/Standard */
  boxShadow: "0px 12px 24px 0px rgba(216, 216, 218, 0.80)",
}));

export const BalanceChartTimeFilterOptionWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
});

export const BalanceChartTimeFilterOptionCheckbox = styled("div")(({ theme }) => ({
  display: "flex",
  width: "16px",
  height: "16px",
  alignItems: "center",
  gap: "4px",
  flexShrink: 0,
  borderRadius: "4px",
  border: "1px solid  #002035",
  background: theme.palette.background.paper,
}));

export const BalanceChartTimeFilterOptionName = styled("div")({
  flex: "1 0 0",
  color: "#002035",
  fontSize: "12px",
});

export const BalanceChartTimeFilterClearButton = styled("div")({
  fontSize: "12px",
  display: "flex",
  alignSelf: "center",
});

interface PortfolioBalanceProps {
  balanceData: IBalanceData;
  chartData: LineData[];
  bitcoinData: LineData[];
  ethereumData: LineData[];
  holdingsData: ChartType[];
  isCompareActive: boolean;
  selectedTime: string;
  compareOptions: ICompareOptions[];
  timeFilterOptions: string[];
  timePeriodOptions: ITimePeriodOption[];
  timePeriod: string;
  handleTimePeriod: (newOpt: ITimePeriodOption) => void;
  onCompareClick: () => void;
  onToggleCompareOptions: (optionName: string) => void;
  onCompareClear: () => void;
  OnTimeFilterClick: (time: string) => void;
}

const PortfolioBalance = (props: PortfolioBalanceProps) => {
  const {
    balanceData,
    chartData,
    bitcoinData,
    ethereumData,
    holdingsData,
    selectedTime,
    compareOptions,
    timeFilterOptions,
    OnTimeFilterClick,
  } = props;

  const { bestPerformers, balance, change, dollarChange, unformattedValues } = balanceData;
  const { isMobile } = useDeviceSizes();
  const theme = useTheme();

  const handleAmplitudeTracking = () => {
    trackEvent(`Clicked My Balance Button`, undefined, "PortfolioBalance");
  };

  return (
    <>
      {/* BALANCE */}
      <BalanceWrapper>
        <BalanceFunds>
          {/* BALANCE HEADING */}
          <BalanceHeading>
            <BalanceText to="/wallets" onClick={handleAmplitudeTracking}>
              My balance
              <RightArrowIcon />
            </BalanceText>
            <BalanceTextAmount id="user-balance-amount" translate="no">
              {balance}
            </BalanceTextAmount>
            <BalanceTextPercent>
              <BalanceOverviewChange
                change={change}
                dollarChange={dollarChange}
                unformattedChange={(unformattedValues.change as number) ?? 0}
              />
            </BalanceTextPercent>
          </BalanceHeading>

          {/* BALANCE DEPOSIT/TRADE BUTTONS */}
          <BalanceButtons>
            <BalanceButton type="deposit" />
            <BalanceButton type="trade" />
          </BalanceButtons>
        </BalanceFunds>

        {/* BEST PERFORMERS */}
        <BestPerformer>
          <BestPerformerHeader>
            Your Best Performers
            <InfoIcon tooltip={BEST_PERFORMERS_TOOLTIP} color={theme.palette.text.primary} />
          </BestPerformerHeader>

          {/* PIE CHART */}
          <BalanceTableWrapper>
            {/* only on desktop and tablet */}
            {!isMobile && <DonutChart data={holdingsData} />}

            <BalanceTable>
              <tbody id="balance-table-body">
                {bestPerformers.map((coin) => (
                  <tr key={coin.name}>
                    <BalanceTableCell firstCell>
                      <BalanceTableRowIcon to={`/coin/${coin.code}`}>
                        {coin.icon}
                        {coin.name}
                      </BalanceTableRowIcon>
                    </BalanceTableCell>
                    <BalanceTableCell translate="no">{coin.amount}</BalanceTableCell>
                    <BalanceTableCell align="right" translate="no">
                      {coin.percent}
                    </BalanceTableCell>
                  </tr>
                ))}
              </tbody>
            </BalanceTable>
          </BalanceTableWrapper>
        </BestPerformer>
      </BalanceWrapper>

      {/* CHART */}
      <BalanceChartWrapper>
        <BalanceChart
          data={chartData}
          bitcoinData={bitcoinData}
          ethereumData={ethereumData}
          compareOptions={compareOptions}
          timeFilterOptions={timeFilterOptions}
        />

        {/* TIME FILTER */}
        <BalanceChartTimeFilter>
          {timeFilterOptions.map((opt) => (
            <BalanceChartTimeFilterOption
              key={opt}
              selected={selectedTime === opt}
              onClick={() => OnTimeFilterClick(opt)}>
              {opt}
            </BalanceChartTimeFilterOption>
          ))}
        </BalanceChartTimeFilter>
      </BalanceChartWrapper>
    </>
  );
};

export default PortfolioBalance;
